import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X } from 'lucide-react';

// Globaler, seitenübergreifender Reservierungs-Countdown.
// BookingForm schreibt die Ablaufzeit nach `localStorage.cartHoldExpiresAt` und
// feuert ein `cartHoldUpdated`-Event. Diese Komponente (im App-Root gemountet)
// zeigt die laufende 30-Min-Reservierung oben am rechten Rand auf JEDER Seite:
// schiebt sich von rechts ein, ist per X einklappbar (kleiner Uhr-Button) und
// wieder aufklappbar. Der Zustand wird je Ablaufzeit in localStorage gemerkt.

const readExpiry = () => {
  const v = localStorage.getItem('cartHoldExpiresAt');
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

const CartHoldCountdown = () => {
  const [expiry, setExpiry] = useState(readExpiry);
  const [remaining, setRemaining] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [shown, setShown] = useState(false); // Slide-In-Animation
  const [overlayOpen, setOverlayOpen] = useState(false); // Dialog/Sheet offen?

  useEffect(() => {
    const check = () => setOverlayOpen(!!document.querySelector('[role="dialog"][data-state="open"]'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const sync = () => {
      const exp = readExpiry();
      setExpiry(exp);
      const col = localStorage.getItem('cartHoldCollapsed');
      setCollapsed(Boolean(exp) && col === String(exp));
    };
    sync();
    window.addEventListener('cartHoldUpdated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('cartHoldUpdated', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!expiry) { setRemaining(0); return; }
    const tick = () => setRemaining(Math.max(0, Math.floor((expiry - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [expiry]);

  const active = Boolean(expiry) && remaining > 0;

  useEffect(() => {
    if (active) {
      const t = setTimeout(() => setShown(true), 30);
      return () => clearTimeout(t);
    }
    setShown(false);
  }, [active]);

  if (!active || overlayOpen) return null;

  const expiring = remaining <= 300;
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const colorCls = expiring
    ? 'bg-red-600 border-red-700 text-white animate-pulse'
    : 'bg-amber-400 border-amber-500 text-sky-950';
  const posCls = 'fixed right-0 top-3 md:top-24 z-[60]';

  const collapse = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem('cartHoldCollapsed', String(expiry)); } catch {}
    setCollapsed(true);
  };
  const expand = () => {
    try { localStorage.removeItem('cartHoldCollapsed'); } catch {}
    setCollapsed(false);
  };

  // Eingeklappt: kleiner Uhr-Button am rechten Rand
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={expand}
        aria-label="Reservierungs-Countdown anzeigen"
        data-testid="global-hold-collapsed"
        className={`${posCls} flex h-11 w-11 items-center justify-center rounded-l-xl shadow-xl border-y border-l ${colorCls}`}
        title={`Reservierung: noch ${mm}:${ss} Min`}
      >
        <Clock className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div
      className={`${posCls} transition-transform duration-500 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}
      data-testid="global-hold-countdown"
    >
      <div className={`flex items-center gap-2 rounded-l-full pl-4 pr-2 py-2.5 shadow-xl border-y border-l ${colorCls}`}>
        <Link
          to="/mietanfrage"
          className="flex items-center gap-2 no-underline text-current"
          title="Zur Mietanfrage – Reservierung abschließen"
          data-testid="global-hold-link"
        >
          <Clock className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm font-bold whitespace-nowrap">Reservierung: {mm}:{ss} Min</span>
        </Link>
        <button
          type="button"
          onClick={collapse}
          aria-label="Hinweis einklappen"
          data-testid="global-hold-close"
          className="ml-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/10 hover:bg-black/25 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default CartHoldCountdown;
